import type { TodosSubmissionsStatus } from "@profile-service-types/index.ts";
import * as Sentry from "@sentry/vue";
import type { UseMutationOptions } from "@tanstack/vue-query";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { StoredFileUpload } from "@/components/MyStudents/Submissions/types";
import { buildUploadSubmissionData } from "@/components/MyStudents/Submissions/utils";
import profileService from "@/services/profile.js";
import { createFileName, s3Upload } from "@/util/s3";
import { TODO_SUBMISSION_QUERY_KEY_PREFIX } from "./useSubmissionReviewData";

export interface UploadSubmissionOnBehalfVariables {
  file: File;
  status: TodosSubmissionsStatus;
  applicationId: string;
  participantId: string;
  orderId: string | undefined;
  todoId: string;
  todoTitle: string;
  studentFirstName: string | undefined;
  studentLastName: string | undefined;
}

export const useUploadSubmissionOnBehalf = (
  useMutationOptions?: UseMutationOptions<void, Error, UploadSubmissionOnBehalfVariables>,
) => {
  const queryClient = useQueryClient();

  return useMutation<void, Error, UploadSubmissionOnBehalfVariables>({
    mutationFn: async ({
      file,
      status,
      applicationId,
      participantId,
      orderId,
      todoId,
      todoTitle,
      studentFirstName,
      studentLastName,
    }) => {
      const newName = createFileName(file.name, todoTitle, studentFirstName, studentLastName);
      const s3 = await s3Upload(file, newName);
      const upload: StoredFileUpload = {
        storage: "s3",
        file_name: newName,
        bucket: s3.Bucket,
        key: s3.key,
        file_path_url: s3.Location,
        acl: "private",
        size: file.size,
        type: file.type,
        original_file_name: file.name,
      };
      await profileService.upsertSubmission({
        application_id: applicationId,
        participant_id: participantId,
        todo_id: todoId,
        order_id: orderId,
        submission_data: buildUploadSubmissionData(todoId, applicationId, upload),
      });
      await profileService.upsertTodoStatusOverride(applicationId, todoId, status);
    },
    onSettled: async () => {
      await queryClient.invalidateQueries({ queryKey: [TODO_SUBMISSION_QUERY_KEY_PREFIX] });
    },
    onError: (uploadError) => {
      Sentry.captureException(uploadError, {
        tags: { fieldName: "todo_submission_on_behalf", troubleshooting_note: `Upload on behalf: ${uploadError}` },
      });
    },
    ...useMutationOptions,
  });
};
